import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { reviewSchema, ReviewFormData } from "@/hooks/useSubmitReview";

interface ReviewFormProps {
  onSubmit: (data: ReviewFormData) => void;
  isLoading?: boolean;
  isGuest?: boolean;
}

export const ReviewForm = ({ onSubmit, isLoading, isGuest }: ReviewFormProps) => {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<ReviewFormData>({
    resolver: zodResolver(reviewSchema),
    defaultValues: {
      rating: 0,
    },
  });

  const handleRatingClick = (value: number) => {
    setRating(value);
    setValue('rating', value);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {/* Rating */}
      <div className="space-y-2">
        <Label>Rating *</Label>
        <div className="flex gap-1">
          {[1, 2, 3, 4, 5].map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => handleRatingClick(value)}
              onMouseEnter={() => setHoverRating(value)}
              onMouseLeave={() => setHoverRating(0)}
              className="transition-transform hover:scale-110"
            >
              <Star
                className={`h-8 w-8 ${
                  value <= (hoverRating || rating)
                    ? "fill-primary text-primary"
                    : "fill-muted text-muted-foreground"
                }`}
              />
            </button>
          ))}
        </div>
        {errors.rating && (
          <p className="text-sm text-destructive">{errors.rating.message}</p>
        )}
      </div>

      {/* Title */}
      <div className="space-y-2">
        <Label htmlFor="title">Review Title *</Label>
        <Input
          id="title"
          placeholder="Sum up your experience"
          {...register("title")}
        />
        {errors.title && (
          <p className="text-sm text-destructive">{errors.title.message}</p>
        )}
      </div>

      {/* Content */}
      <div className="space-y-2">
        <Label htmlFor="content">Your Review *</Label>
        <Textarea
          id="content"
          placeholder="Share your thoughts about this product..."
          rows={5}
          {...register("content")}
        />
        {errors.content && (
          <p className="text-sm text-destructive">{errors.content.message}</p>
        )}
      </div>

      {/* Guest fields */}
      {isGuest && (
        <>
          <div className="space-y-2">
            <Label htmlFor="reviewer_name">Your Name *</Label>
            <Input
              id="reviewer_name"
              placeholder="John Doe"
              {...register("reviewer_name")}
            />
            {errors.reviewer_name && (
              <p className="text-sm text-destructive">{errors.reviewer_name.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="reviewer_email">Your Email *</Label>
            <Input
              id="reviewer_email"
              type="email"
              placeholder="john@example.com"
              {...register("reviewer_email")}
            />
            {errors.reviewer_email && (
              <p className="text-sm text-destructive">{errors.reviewer_email.message}</p>
            )}
          </div>
        </>
      )}

      <Button type="submit" disabled={isLoading} className="w-full">
        {isLoading ? "Submitting..." : "Submit Review"}
      </Button>
    </form>
  );
};
